import React, { useState, useEffect } from "react";
import { NavLink as RouterNavLink } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  TrendingUp,
  Users,
  Building2,
  Activity,
  ArrowRight,
  Clock,
  Loader2,
  Plus,
  FileUp,
  BarChart3,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import Loading from "../components/Loading";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://api.littleexplorers.help";

const quickActions = [
  {
    title: "Add Organization",
    description: "Create a new organization in the system",
    href: "/org-management",
    icon: Plus,
    color: "primary",
  },
  {
    title: "CSV Upload",
    description: "Bulk import data via CSV file",
    href: "/upload",
    icon: FileUp,
    color: "success",
  },
  {
    title: "View Reports",
    description: "Analyze platform metrics and data",
    href: "/reports/home",
    icon: BarChart3,
    color: "warning",
  },
];

const getColorClasses = (color) => {
  const colors = {
    primary: "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400",
    success: "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400",
    warning: "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400",
    danger: "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400",
    purple: "bg-purple-100 text-purple-600 dark:bg-purple-900/30 dark:text-purple-400",
  };
  return colors[color] || colors.primary;
};

const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);

  if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  return `${Math.floor(diffInSeconds / 86400)}d ago`;
};

const getActivityIcon = (type) => {
  switch (type) {
    case "csv_upload_completed":
      return <CheckCircle2 className="w-5 h-5 text-green-500" />;
    case "csv_upload_failed":
      return <AlertCircle className="w-5 h-5 text-red-500" />;
    case "organization_created":
      return <Building2 className="w-5 h-5 text-blue-500" />;
    case "organization_updated":
      return <Activity className="w-5 h-5 text-purple-500" />;
    default:
      return <Activity className="w-5 h-5 text-gray-500" />;
  }
};

const getActivityColor = (type) => {
  switch (type) {
    case "csv_upload_completed":
      return "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800";
    case "csv_upload_failed":
      return "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800";
    case "organization_created":
      return "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800";
    case "organization_updated":
      return "bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800";
    default:
      return "bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700";
  }
};

const DashboardComponent = () => {
  const { user, getAccessToken } = useAuth();
  const [recentActivity, setRecentActivity] = useState([]);
  const [isLoadingActivity, setIsLoadingActivity] = useState(true);
  const [activityError, setActivityError] = useState(null);
  const [stats, setStats] = useState([
    { name: "Total Organizations", value: "0", icon: Building2, color: "blue", trend: null },
    { name: "Verified", value: "0", icon: CheckCircle2, color: "green", trend: null },
    { name: "Programs", value: "0", icon: Activity, color: "purple", trend: null },
    { name: "Active Users", value: "—", icon: Users, color: "amber", trend: null },
  ]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const token = getAccessToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/v2/programs/stats/overview`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch stats");

        const data = await response.json();
        
        setStats([
          { 
            name: "Organizations", 
            value: data.totalOrganizations?.toLocaleString() || "0", 
            icon: Building2, 
            color: "blue",
            trend: "+12%"
          },
          { 
            name: "Verified", 
            value: data.verifiedOrganizations?.toLocaleString() || "0", 
            icon: CheckCircle2, 
            color: "green",
            trend: "+8%"
          },
          { 
            name: "Programs", 
            value: data.totalPrograms?.toLocaleString() || "0", 
            icon: Activity, 
            color: "purple",
            trend: "+24%"
          },
          { 
            name: "Growth", 
            value: "24%", 
            icon: TrendingUp, 
            color: "amber",
            trend: "This month"
          },
        ]);
      } catch (err) {
        console.error("Error fetching stats:", err);
      }
    };

    fetchStats();
  }, [getAccessToken]);

  useEffect(() => {
    const fetchActivities = async () => {
      try {
        setIsLoadingActivity(true);
        const token = getAccessToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/activities?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!response.ok) throw new Error("Failed to fetch activities");

        const data = await response.json();
        setRecentActivity(data.activities || []);
      } catch (err) {
        console.error("Error fetching activities:", err);
        setActivityError(err.message);
      } finally {
        setIsLoadingActivity(false);
      }
    };

    fetchActivities();
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, [getAccessToken]);

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text-primary)] mb-1">
            Welcome back, <span className="text-gradient">{user?.name?.split(' ')[0] || "Admin"}</span>
          </h1>
          <p className="text-[var(--text-tertiary)]">
            Here's what's happening with your platform today
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span>All systems operational</span>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={stat.name} className="stat-card animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-sm font-medium text-[var(--text-tertiary)] mb-1">{stat.name}</p>
                  <p className="text-3xl font-bold text-[var(--text-primary)]">{stat.value}</p>
                  {stat.trend && (
                    <p className="text-xs text-green-500 mt-1 flex items-center gap-1">
                      <TrendingUp className="w-3 h-3" />
                      {stat.trend}
                    </p>
                  )}
                </div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getColorClasses(stat.color)}`}>
                  <Icon className="w-5 h-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <div className="lg:col-span-2 space-y-4">
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Quick Actions</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {quickActions.map((action, index) => {
              const Icon = action.icon;
              return (
                <RouterNavLink
                  key={action.title}
                  to={action.href}
                  className="card p-5 group hover:scale-[1.02] transition-transform"
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${getColorClasses(action.color)}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-[var(--text-primary)] mb-1">{action.title}</h3>
                  <p className="text-sm text-[var(--text-tertiary)] mb-4">{action.description}</p>
                  <div className="flex items-center text-sm font-medium text-[var(--primary-600)] group-hover:gap-2 transition-all">
                    <span>Get started</span>
                    <ArrowRight className="w-4 h-4 ml-1" />
                  </div>
                </RouterNavLink>
              );
            })}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-[var(--text-primary)]">Recent Activity</h2>
            <button type="button" className="text-sm text-[var(--primary-600)] hover:text-[var(--primary-700)] font-medium">
              View all
            </button>
          </div>
          
          <div className="card p-4">
            {isLoadingActivity ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[var(--primary-500)] animate-spin" />
              </div>
            ) : activityError ? (
              <div className="text-center py-8 text-[var(--text-tertiary)]">
                <AlertCircle className="w-8 h-8 mx-auto mb-2 text-red-500" />
                <p className="text-sm">Failed to load activities</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-tertiary)]">
                <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No recent activity</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentActivity.map((activity, index) => (
                  <div
                    key={activity.id || index}
                    className={`flex items-start gap-3 p-3 rounded-lg border ${getActivityColor(activity.type)}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-[var(--text-primary)] leading-snug">
                        {activity.description}
                      </p>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        {formatRelativeTime(activity.createdAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardComponent;
